import first from "lodash/first";
import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { ZIndexLayer } from "shared/lib/styles";

import type { articleSpeechInlineWidgetArticle$key } from "scmp-app/queries/__generated__/articleSpeechInlineWidgetArticle.graphql";

import { ArticleSpeechInlineWidgetArticle20 } from "./variants/article-2.0";
import { ArticleSpeechInlineWidgetGeneric } from "./variants/generic";
import type { HeadphoneIconTheme } from "./variants/generic/button/headphone-icon";

export type ArticleSpeechInlineWidgetProps = {
  className?: string;
  headphoneIconTheme?: HeadphoneIconTheme;
  onClick?: () => void;
  reference: articleSpeechInlineWidgetArticle$key;
  variant: "article-2.0-minify" | "generic" | "generic-minify";
  withMinifyCleanUpButton?: boolean;
  withMinifyText?: boolean;
  zIndex?: string;
};

export const ArticleSpeechInlineWidget: FunctionComponent<ArticleSpeechInlineWidgetProps> = ({
  className,
  headphoneIconTheme = "grey",
  onClick,
  reference,
  variant,
  withMinifyCleanUpButton = true,
  withMinifyText = true,
  zIndex = `${ZIndexLayer.Drawer - 1}`,
}) => {
  const article = useFragment(
    graphql`
      fragment articleSpeechInlineWidgetArticle on Article {
        articleSpeeches {
          url
        }
        ...genericArticleSpeechInlineWidgetGenericArticle
      }
    `,
    reference,
  );

  const hasSpeech = first(article.articleSpeeches ?? [])?.url;

  const handleRenderInlineWidget = () => {
    if (!hasSpeech) {
      return null;
    }

    switch (variant) {
      case "article-2.0-minify":
        return (
          <ArticleSpeechInlineWidgetArticle20
            className={className}
            extendClick={onClick}
            headphoneIconTheme={headphoneIconTheme}
            isMinify={true}
            keepMinifyText={true}
            reference={article}
            withMinifyCleanUpButton={withMinifyCleanUpButton}
            withMinifyText={withMinifyText}
            zIndex={zIndex}
          />
        );
      case "generic-minify":
        return (
          <ArticleSpeechInlineWidgetGeneric
            className={className}
            extendClick={onClick}
            headphoneIconTheme={headphoneIconTheme}
            isMinify={true}
            reference={article}
            withMinifyText={withMinifyText}
            zIndex={zIndex}
          />
        );
      default:
        return (
          <ArticleSpeechInlineWidgetGeneric
            className={className}
            extendClick={onClick}
            headphoneIconTheme={headphoneIconTheme}
            reference={article}
            zIndex={zIndex}
          />
        );
    }
  };
  return handleRenderInlineWidget();
};

ArticleSpeechInlineWidget.displayName = "ArticleSpeechInlineWidget";
